import { Component, Fragment, h, Host, Prop, State, Watch } from '@stencil/core'
import Axios from 'axios'
import { debounce } from 'lodash-es'
import { IAiNote, IAiNoteSource, IAiTemplate, IAiTemplateModifier } from 'src/ai-note/types'
import { AppSharedPropsStore } from 'src/app'
import { flashToast } from 'src/app/toast'
import { copyToClipboardWithProvider } from 'src/call/clipboard/clipboard'
import { dayjs } from 'src/lib/dayjs'
import { classes } from 'src/utils/css'

import { AiNoteClipboardMenu, aiNoteProviders, ClipboardStore } from '../notes/ui'
import { CallStore } from '../state'
import { ICallAiNoteTemplate, InitialOpenModal } from '../types'
import { AiNoteTemplateButton, Banner } from './ui'
import { isInstantiated } from './utils'

// Average completion time for ~300 samples as of 16 Aug 2023
const MAX_PROGRESS_BAR_SECONDS = 35

interface IProgress {
  aiTemplate: IAiTemplate
  startSeconds: number
  startPercent: number
  currentPercent: number
}

@Component({
  tag: 'page-call-detail-ai-notes',
})
export class PageCallDetailAiNotes {
  @Prop() hasTemplateModifiers = false
  @Prop() showAiModifierBanner = false
  @Prop() defaultTemplatedNoteLanguage?: string
  @Prop() canCreateTranslations = false
  @Prop() initialOpenModal: InitialOpenModal | null = null
  @State() selectedAiNoteSource: IAiNoteSource
  @State() selectedAiNote: IAiNote
  @State() selectedAiNoteProgress: IProgress
  @State() selectedAiTemplate: IAiTemplate
  @State() showApplyModifierDialog = false
  @State() savingModifierChanges = false
  @State() selectedLanguage = this.defaultTemplatedNoteLanguage || 'unknown'

  handleAiTemplateUpdateError(ex: any) {
    //If the operation fails, then revert to a usable state
    this.setUsableState()
    if (ex.response && ex.response.status === 403) {
      CallStore.state.requiredPlan =
        ex.response.data?.requiredPlan || AppSharedPropsStore?.state?.currentUser?.requiredAdvancedAiPlan
    } else {
      flashToast({ type: 'alert', text: 'Please try again' })
    }
  }

  @Watch('selectedAiNote')
  onSelectedAiNote() {
    if (!this.selectedAiNote) {
      return
    }
    if (this.selectedAiNote?.isGenerating) {
      this.makeProgress()
    }
  }

  @Watch('defaultTemplatedNoteLanguage')
  onDefaultLanguage() {
    // This watch is required as aiNotes is lazy loaded, so defaultTemplatedNoteLanguage will come in later
    if (this.defaultTemplatedNoteLanguage) {
      this.selectedLanguage = this.defaultTemplatedNoteLanguage
    }
  }

  @Watch('selectedAiNoteSource')
  @Watch('selectedLanguage')
  async onSelectedLanguage() {
    // If no source is selected, no note can possibly be selected.
    if (!this.selectedAiNoteSource) {
      this.selectedAiNote = null
      return
    }
    // Hot path if we're not trying to view a translated note:
    if (this.selectedLanguage === 'unknown') {
      this.selectedAiNote = this.selectedAiNoteSource
      return
    }

    // Try and find the note from existing selectedAiNoteSource.translations:
    const selectedAiNote = this.selectedAiNoteSource.translations?.find(
      (translation) => translation.language === this.selectedLanguage
    )

    if (selectedAiNote) {
      this.selectedAiNote = selectedAiNote
      return
    }

    if (!CallStore.state.aiNotes?.actions?.createUrl) {
      this.setUsableState()
      return
    }

    // Since the translation does not exist, try to create it:
    await this.createTemplatedNote()
  }

  @Watch('selectedAiTemplate')
  async onSelectedAiTemplate() {
    // If no template is selected, no note is selected.
    // Should be reachable only if a create fails.
    if (!this.selectedAiTemplate) {
      this.selectedAiNoteSource = null
      return
    }

    // If an aiNote exists for the selected template, then find it.
    const aiNote = CallStore.state.aiNotes?.notes?.find((aiNote) => aiNote.aiTemplate.id === this.selectedAiTemplate.id)
    if (aiNote) {
      this.selectedAiNoteSource = aiNote
      return
    }
    // If user cannot create an aiNote, then revert to a usable state.
    if (!CallStore.state.aiNotes?.actions?.createUrl) {
      this.setUsableState()
      return
    }
    // If no aiNote is found for the selected template, then create it.
    await this.createTemplatedNote()
  }

  onUpdateAiNotes = debounce(
    () => {
      if (CallStore.state.aiNotes?.notes) {
        // Runs any time CallStore.state.aiNotes is changed
        if (this.selectedAiTemplate) {
          // Fires when:
          // - templated notes are created (pusher event from page-call-detail), see TemplatedNote::CreateOnce
          // - templated notes (and translations) get successful response back from create api, see this.createTemplatedNote
          // - templated note finishes generation (pusher event from page-call-detail)
          this.selectedAiNoteSource = CallStore.state.aiNotes.notes.find(
            (aiNote) => aiNote.aiTemplate.id === this.selectedAiTemplate.id
          )
        } else if (CallStore.state.aiNotes.notes.length > 0) {
          // You should only reach this block on load when selectedAiTemplate is blank:
          const defaultNote = CallStore.state.aiNotes.defaultNote ?? CallStore.state.aiNotes.notes[0]
          this.selectedAiTemplate = defaultNote.aiTemplate
        }

        // Updates to loading state:
        if (this.selectedAiNoteProgress) {
          const aiNoteSource = CallStore.state.aiNotes.notes.find(
            (aiNote) => aiNote.aiTemplate.id === this.selectedAiNoteProgress.aiTemplate.id
          )
          if (!aiNoteSource) {
            return
          }
          let aiNote = aiNoteSource as IAiNote
          if (this.selectedLanguage !== 'unknown') {
            aiNote = aiNoteSource.translations?.find((translation) => translation.language === this.selectedLanguage)
          }
          if (aiNote?.isReady) {
            this.selectedAiNoteProgress = null
          }
        }
      }
    },
    1000,
    { leading: true, trailing: true }
  )

  async createTemplatedNote() {
    try {
      const response = await Axios.post<IAiNoteSource>(CallStore.state.aiNotes.actions.createUrl, {
        template_id: this.selectedAiTemplate.id,
        language: this.selectedLanguage,
      })
      // Always set the new source templated_note so we have the new list of translations
      this.selectedAiNoteSource = response.data

      const existingAiNoteSource = CallStore.state.aiNotes.notes.find(
        (aiNote) => aiNote.aiTemplate.id === this.selectedAiNoteProgress.aiTemplate.id
      )

      if (existingAiNoteSource) {
        // Update existing source templated note (in the case of a new translation):
        CallStore.state.aiNotes = {
          ...CallStore.state.aiNotes,
          notes: CallStore.state.aiNotes.notes.map((aiNoteSource) => {
            if (aiNoteSource.id === existingAiNoteSource.id) {
              return response.data
            }
            return aiNoteSource
          }),
        }
      } else {
        // Brand new source templated note:
        CallStore.state.aiNotes = {
          ...CallStore.state.aiNotes,
          notes: [...CallStore.state.aiNotes.notes, response.data],
        }
      }
    } catch (ex) {
      this.handleAiTemplateUpdateError(ex)
    }
  }

  async applyTemplateModifier(modifier: string) {
    if (!this.selectedAiNoteSource.actions.applyModifierUrl) {
      return
    }
    try {
      this.savingModifierChanges = true
      const response = await Axios.post<IAiNoteSource>(this.selectedAiNoteSource.actions.applyModifierUrl, {
        modifier,
      })
      const remainingNotes = CallStore.state.aiNotes.notes.filter((note) => note.id !== this.selectedAiNoteSource.id)
      this.selectedAiNoteSource = response.data
      CallStore.state.aiNotes = {
        ...CallStore.state.aiNotes,
        notes: [...remainingNotes, this.selectedAiNoteSource],
      }
    } catch (ex) {
      this.handleAiTemplateUpdateError(ex)
    } finally {
      this.savingModifierChanges = false
    }
  }

  async removeTemplateModifier() {
    if (!this.selectedAiNoteSource.actions.removeModifierUrl) {
      return
    }
    try {
      this.savingModifierChanges = true
      const response = await Axios.delete<IAiNoteSource>(this.selectedAiNoteSource.actions.removeModifierUrl)
      const remainingNotes = CallStore.state.aiNotes.notes.filter((note) => note.id !== this.selectedAiNoteSource.id)
      this.selectedAiNoteSource = response.data
      CallStore.state.aiNotes = {
        ...CallStore.state.aiNotes,
        notes: [...remainingNotes, this.selectedAiNoteSource],
      }
    } catch (ex) {
      this.handleAiTemplateUpdateError(ex)
    } finally {
      this.savingModifierChanges = false
    }
  }

  async publishTemplateModifier() {
    if (!this.selectedAiNoteSource.modifier.actions.publishUrl) {
      return
    }
    try {
      this.savingModifierChanges = true
      const response = await Axios.put<IAiTemplateModifier>(this.selectedAiNoteSource.modifier.actions.publishUrl)
      this.selectedAiNoteSource.modifier = response.data
    } catch (ex) {
      this.handleAiTemplateUpdateError(ex)
    } finally {
      this.savingModifierChanges = false
    }
  }

  async applyGlobalModifier() {
    if (!this.selectedAiNoteSource.actions.applyGlobalModifierUrl) {
      return
    }
    try {
      this.savingModifierChanges = true
      const response = await Axios.put<IAiNoteSource>(this.selectedAiNoteSource.actions.applyGlobalModifierUrl)
      const remainingNotes = CallStore.state.aiNotes.notes.filter((note) => note.id !== this.selectedAiNoteSource.id)
      this.selectedAiNoteSource = response.data
      CallStore.state.aiNotes = {
        ...CallStore.state.aiNotes,
        notes: [...remainingNotes, this.selectedAiNoteSource],
      }
    } catch (ex) {
      this.handleAiTemplateUpdateError(ex)
    } finally {
      this.savingModifierChanges = false
    }
  }

  makeProgress() {
    let startSeconds = dayjs().diff(dayjs(this.selectedAiNote.createdAt), 's')
    if (startSeconds > MAX_PROGRESS_BAR_SECONDS) {
      startSeconds = MAX_PROGRESS_BAR_SECONDS
    } else if (startSeconds < 0) {
      startSeconds = 0
    }
    let startPercent = startSeconds / MAX_PROGRESS_BAR_SECONDS
    if (startPercent > 0.99) {
      startPercent = 0.99
    }
    this.selectedAiNoteProgress = {
      aiTemplate: this.selectedAiTemplate,
      startSeconds: startSeconds,
      startPercent: startPercent,
      currentPercent: startPercent,
    }
  }

  setUsableState() {
    if (CallStore.state.aiNotes?.notes?.length > 0) {
      this.selectedAiTemplate =
        CallStore.state.aiNotes.defaultNote?.aiTemplate ?? CallStore.state.aiNotes.notes[0].aiTemplate
    } else {
      this.selectedAiTemplate = null
    }
  }

  componentWillLoad() {
    CallStore.onChange('aiNotes', () => this.onUpdateAiNotes())
    this.onUpdateAiNotes()
    if (!ClipboardStore.state.aiNoteProvider) {
      ClipboardStore.state.aiNoteProvider = aiNoteProviders[0]
    }
  }

  bannerPerhaps = (className: string) => {
    return CallStore.state.aiNotes.banner ? (
      <Banner class={`mt-15px ${className}`} banner={CallStore.state.aiNotes.banner} />
    ) : null
  }

  updateSelectedOrError(aiNoteTemplate: ICallAiNoteTemplate) {
    if (isInstantiated(aiNoteTemplate, CallStore.state.aiNotes.notes) || aiNoteTemplate?.isAvailable) {
      this.selectedAiTemplate = aiNoteTemplate
    } else {
      CallStore.state.requiredPlan = AppSharedPropsStore?.state?.currentUser?.requiredAdvancedAiPlan
    }
  }

  render() {
    if (!CallStore.state.aiNotes?.notes) {
      return null
    }
    if (CallStore.state.aiNotes.notes.length > 0 && !this.selectedAiNote) {
      // CallStore has aiNotes but the (debounced) onChange callback has not completed yet
      return null
    }

    // Until the call is finalized, fake like we're generating the AI note in the background
    // On Processed doesn't actually trigger the creation of the templated_note until the call is finalized
    // This covers time between ready --> finalized
    if (CallStore.state.call.state === 'ready') {
      return (
        <Host class='flex overflow-y-hidden flex-col flex-grow'>
          <div class='flex flex-col items-center pr-20px mt-20 spacing-y-14px'>
            <div class='flex text-default font-semibold text-white'>0%</div>
            <ui-progress
              key='testing'
              class='flex-shrink-0 h-3px rounded-large'
              style={{ width: '208px' }}
              color='brand-base'
              mockSeconds={MAX_PROGRESS_BAR_SECONDS}
              startPercent={0}
            />
            <div class='flex text-default font-semibold text-gray-100'>Please wait, AI note is generating&hellip;</div>
          </div>
        </Host>
      )
    }

    if (CallStore.state.aiNotes.notes.length === 0) {
      if (CallStore.state.aiNotes.templates.length === 0) {
        // Nothing we can show or create
        return null
      }

      return (
        <Host class='flex overflow-y-hidden flex-col flex-grow'>
          {this.bannerPerhaps('mx-20px')}
          <ui-scrollarea shadowColor='transparent' shadowStyleY={{ display: 'none' }} overflowX={false}>
            <div
              class={`inline-flex flex-col gap-8px mx-20px flex-start ${classes(
                !!CallStore.state.aiNotes.banner,
                'mt-12px',
                'mt-20px'
              )}`}
            >
              <div class='flex gap-10px self-stretch flex-start'>
                <span class='text-2xsmall font-extrabold text-gray-400 uppercase'>Select Template</span>
              </div>
              <div class='flex flex-wrap gap-8px content-start items-start'>
                {CallStore.state.aiNotes.templates.map((aiNoteTemplate) => {
                  return (
                    <AiNoteTemplateButton
                      aiNoteTemplate={aiNoteTemplate}
                      onClick={() => {
                        this.updateSelectedOrError(aiNoteTemplate)
                      }}
                    />
                  )
                })}
              </div>
            </div>
          </ui-scrollarea>
        </Host>
      )
    }
    const canUseModifier = this.hasTemplateModifiers && this.selectedAiTemplate?.isModifierAvailable
    const modifierNoteToShow = (() => {
      if (!CallStore.state.aiNotes.canEditTemplateModifier || !canUseModifier || this.selectedAiNote.isGenerating) {
        return 'none'
      }
      switch (true) {
        case !!this.selectedAiNoteSource.actions.applyGlobalModifierUrl:
        // fallthrough
        case this.selectedAiNoteSource.isModifierDeleted:
          return 'modifier_updated'
        case !this.selectedAiNoteSource.modifier:
          return 'modifier_can_create'
        case !this.selectedAiNoteSource.modifier.isPublished:
          return 'modifier_unpublished'
        default:
          return 'none'
      }
    })()

    return (
      <Host class='flex overflow-y-hidden flex-col flex-grow gap-y-15px pl-20px'>
        {this.bannerPerhaps('mr-20px')}
        <div class={`flex items-center pr-20px ${classes(!CallStore.state.aiNotes.banner, 'mt-20px')}`}>
          {/* Only show selection if multiple templates are available */}
          {CallStore.state.aiNotes.templates.length > 1 && (
            <div class='flex items-center spacing-x-12px'>
              <call-select-ai-template
                initialOpenModal={this.initialOpenModal}
                templates={CallStore.state.aiNotes.templates}
                notes={CallStore.state.aiNotes.notes}
                canEditTemplateModifier={CallStore.state.aiNotes.canEditTemplateModifier}
                applyModifier={
                  canUseModifier && this.selectedAiNoteSource.actions.applyModifierUrl
                    ? async (modifier: string) => {
                        await this.applyTemplateModifier(modifier)
                      }
                    : null
                }
                removeTemplateModifier={
                  canUseModifier && this.selectedAiNoteSource.actions.removeModifierUrl
                    ? async () => {
                        await this.removeTemplateModifier()
                      }
                    : null
                }
                applyGlobalModifier={
                  canUseModifier && this.selectedAiNoteSource.actions.applyGlobalModifierUrl
                    ? async () => {
                        await this.applyGlobalModifier()
                      }
                    : null
                }
                selectedAiTemplate={
                  this.selectedAiTemplate
                    ? CallStore.state.aiNotes.templates.find(
                        (aiTemplate) => aiTemplate.id === this.selectedAiTemplate.id
                      )
                    : null
                }
                onSelected={({ detail: selectedAiTemplate }: CustomEvent<IAiTemplate>) => {
                  const selectedAiNoteTemplate = CallStore.state.aiNotes.templates.find(
                    (aiNoteTemplate) => aiNoteTemplate.id === selectedAiTemplate.id
                  )
                  this.updateSelectedOrError(selectedAiNoteTemplate)
                }}
              />
            </div>
          )}
          {CallStore.state.aiNotes?.actions?.createUrl && this.canCreateTranslations && (
            <div class={'ml-2'}>
              <call-select-translation
                languages={CallStore.state.aiNotes.availableLanguages}
                selectedLanguage={this.selectedLanguage}
                onSelected={({ detail: language }: CustomEvent<string>) => {
                  this.selectedLanguage = language
                }}
                isDisabled={this.selectedAiNoteSource.isGenerating}
              />
            </div>
          )}
          {this.selectedAiNote.isReady && !!this.selectedAiNote.actions?.copyUrl && (
            <div class='flex items-center ml-auto'>
              <button
                class='flex justify-between items-center py-1 px-2 text-base font-bold text-link hover:text-surface-base normal-case bg-link hover:bg-link bg-opacity-10 rounded-md cursor-pointer spacing-x-8px whitespace-no-wrap'
                onClick={() => {
                  if (ClipboardStore.state.aiNoteProvider) {
                    copyToClipboardWithProvider(
                      this.selectedAiNote.actions.copyUrl,
                      ClipboardStore.state.aiNoteProvider.name
                    )
                  }
                }}
              >
                <span>Copy Summary</span>
                <div class='flex relative items-center p-4px px-1 -my-2px -mr-1 ml-2 bg-surface-base rounded pointer-events-auto'>
                  <img src={ClipboardStore.state.aiNoteProvider.iconUrl} class='w-4 h-4' />
                  <AiNoteClipboardMenu
                    onProviderSelected={(_) =>
                      copyToClipboardWithProvider(
                        this.selectedAiNote.actions.copyUrl,
                        ClipboardStore.state.aiNoteProvider.name
                      )
                    }
                  />
                </div>
              </button>
            </div>
          )}
        </div>
        {modifierNoteToShow === 'modifier_can_create' && this.showAiModifierBanner && (
          <div class='flex items-center pr-20px'>
            <div class='p-2 w-full text-xs font-semibold text-yellow bg-yellow bg-opacity-20 rounded-md'>
              <span class='font-bold'>✨ NEW:</span> Customize this summary by clicking the ⚙️ icon above
            </div>
          </div>
        )}

        {modifierNoteToShow === 'modifier_updated' && (
          <div class='flex items-center pr-20px'>
            <div class='p-2 w-full text-xs font-semibold text-yellow bg-yellow bg-opacity-20 rounded-md'>
              <span class='font-bold'>✨</span> There's a new version of the summary available. Click ⚙️ above to
              regenerate
            </div>
          </div>
        )}

        {modifierNoteToShow === 'modifier_unpublished' && (
          <div class='flex items-center pr-20px'>
            <div class='flex justify-between items-center p-2 w-full text-xs font-semibold text-white bg-gray-900 rounded-md'>
              <span>✨ Customized summary generated</span>
              <div class='flex gap-2 items-center'>
                <button
                  class='py-4px px-8px text-brand hover:bg-brand hover:bg-opacity-10 rounded-md border border-brand'
                  disabled={this.savingModifierChanges}
                  onClick={() => this.publishTemplateModifier()}
                >
                  Apply to Future Summaries
                </button>
                <button
                  style={{ width: '26px', height: '26px' }}
                  class='flex justify-center items-center bg-gray-600 hover:bg-gray-400 rounded-md'
                  onClick={() => (this.showApplyModifierDialog = true)}
                  disabled={this.savingModifierChanges}
                >
                  <ui-icon icon='pencil-fill' />
                  <ui-infotip>Edit & regenerate</ui-infotip>
                </button>
                <button
                  style={{ width: '26px', height: '26px' }}
                  class='flex justify-center items-center bg-gray-600 hover:bg-gray-400 rounded-md'
                  onClick={() => this.removeTemplateModifier()}
                  disabled={this.savingModifierChanges}
                >
                  <ui-icon icon='arrow-90deg-left' />
                  <ui-infotip>Restore</ui-infotip>
                </button>
              </div>
            </div>
          </div>
        )}

        {this.selectedAiNote.isGenerating ? (
          <div class='flex flex-col items-center pr-20px mt-20 spacing-y-14px'>
            {this.selectedAiNoteProgress && (
              <Fragment>
                <div class='flex text-default font-semibold text-white'>{`${Math.min(
                  99,
                  Math.round(this.selectedAiNoteProgress.currentPercent * 100)
                )}%`}</div>
                <ui-progress
                  key={this.selectedAiNote.id}
                  class='flex-shrink-0 h-3px rounded-large'
                  style={{ width: '208px' }}
                  color='brand-base'
                  mockSeconds={MAX_PROGRESS_BAR_SECONDS - this.selectedAiNoteProgress.startSeconds}
                  startPercent={this.selectedAiNoteProgress.startPercent}
                  onUpdated={({ detail: updatedPercent }: CustomEvent<number>) => {
                    if (this.selectedAiNoteProgress) {
                      this.selectedAiNoteProgress = {
                        ...this.selectedAiNoteProgress,
                        currentPercent: updatedPercent,
                      }
                    }
                  }}
                />
              </Fragment>
            )}
            <div class='flex text-default font-semibold text-gray-100'>Please wait, AI note is generating&hellip;</div>
          </div>
        ) : (
          <ui-scrollarea shadowColor='transparent' shadowStyleY={{ display: 'none' }} overflowX={false}>
            <div class='pr-20px pb-7'>
              <preview-markdown small source={this.selectedAiNote.noteText} />
            </div>
          </ui-scrollarea>
        )}
        {this.showApplyModifierDialog && this.selectedAiTemplate && this.selectedAiNote.actions.applyModifierUrl && (
          <apply-ai-template-modifier-dialog
            templateName={this.selectedAiTemplate.name}
            canEdit={CallStore.state.aiNotes.canEditTemplateModifier}
            onCancel={() => (this.showApplyModifierDialog = false)}
            modifier={this.selectedAiNoteSource?.modifier?.modifier}
            applyModifier={async (modifier: string) => {
              await this.applyTemplateModifier(modifier)
            }}
          />
        )}
      </Host>
    )
  }
}
